/**
 * Custom-view list contexts are stored as **bare arrays** — `[{...}, {...}]`.
 *
 * Everything that produces a multi-row context (Multi-Select and Filter List
 * additional contexts in "List of Records" mode, empty contexts created with
 * contextType 'list', instruction-driven refreshes with limit !== 1, a Filter
 * List's all-rows context, a repeating section's stored rows, a Data Entry
 * Table's rows) writes that same shape, so consumers only need `Array.isArray`.
 *
 * Earlier builds wrapped list contexts as `{ records: [...], __metadata: {...} }`.
 * Nothing produces that wrapper any more, and it was never persisted (contextData
 * is reset to `{}` every time a view loads) nor sent to the backend. The helpers
 * below still accept it on READ so a context left over in memory — or a shape from
 * an older build mid-session — degrades gracefully instead of rendering blank.
 * Never write the wrapper.
 *
 * Everything the wrapper's `__metadata` carried is derivable from the array itself:
 * `isList` → `Array.isArray`, `isEmptyContext` → `length === 0`, `recordCount` →
 * `length`. Per-row newness lives on the rows (`__isNew` / `__metadata.isNewRecord`).
 */

/** True when `value` is a list context — a bare array, or a legacy wrapper. */
export function isListContext(value: any): boolean {
  return Array.isArray(value)
    || !!(value && typeof value === 'object' && Array.isArray(value.records))
    || !!(value && typeof value === 'object' && value.__metadata?.isList === true);
}

/**
 * Normalize a list context to its rows, or return null when `value` isn't a list
 * (a single-record context object, null, a primitive). Callers that want an array
 * either way should use {@link toContextArrayOrEmpty}.
 */
export function toContextArray(value: any): any[] | null {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object' && Array.isArray(value.records)) {
    return value.records;
  }
  return null;
}

/** Like {@link toContextArray} but yields `[]` instead of null for non-list values. */
export function toContextArrayOrEmpty(value: any): any[] {
  return toContextArray(value) ?? [];
}

/**
 * First row of a list context, or the value itself when it's a single-record
 * context. Returns null for an empty list. Use where a cell needs one record
 * but its binding may point at either shape.
 */
export function firstContextRecord(value: any): any {
  const records = toContextArray(value);
  if (records) {
    return records.length > 0 ? records[0] : null;
  }
  return value ?? null;
}
